import React from 'react';

import { badgeIconMapping } from 'buyside/components/presentation/Badges/Badge/badge';

import {
  allocatorProfileUrl,
  contestUrl,
  employerProfileUrl,
  employerProfileVanityUrl,
  ideaCommentsUrl,
  ideaUrl,
  ideaUpdatesUrl,
  contributorDashboardIdeasUrl,
  jobVaultAppsReceived,
  jobVaultPostingUrl,
  messageUrl,
  newViewerIntroRequestMessageUrl,
  postUrl,
  rankingsUrl,
  userAccountViewerIntroRequestsUrl,
  userUrl,
  userWatchlistUrl,
  whitePaperShowUrl
} from 'buyside/util/urlHelper';

import ArrowsUpDownIcon from 'buyside/svgs/arrows_up_down.svg';
import BriefcaseIcon from 'buyside/svgs/briefcase.svg';
import ClockIcon from 'buyside/svgs/clock.svg';
import DollarSignIcon from 'buyside/svgs/dollar_sign.svg';
import DownArrowIcon from 'buyside/svgs/down_arrow.svg';
import EnvelopeIcon from 'buyside/svgs/envelope.svg';
import EyeIcon from 'buyside/svgs/eye.svg';
import LightbulbIcon from 'buyside/svgs/lightbulb.svg';
import MessageBubbleDoubleIcon from 'buyside/svgs/message_bubble_double.svg';
import MessageBubbleIcon from 'buyside/svgs/message_bubble.svg';
import PenAndPaperIcon from 'buyside/svgs/pen_and_paper.svg';
import PieChartIcon from 'buyside/svgs/pie_chart.svg';
import ScaleIcon from 'buyside/svgs/scale.svg';
import StarIcon from 'buyside/svgs/star.svg';
import TrendingIcon from 'buyside/svgs/trending.svg';
import TrophyIcon from 'buyside/svgs/trophy.svg';
import UpArrowIcon from 'buyside/svgs/up_arrow.svg';
import PaperStack from 'buyside/svgs/paper_stack.svg';

export const getLinkUrl = (notificationClass, linkId, currentUserId, customLink) => {
  switch (notificationClass) {
    // Allocator
    case 'new_allocator_recommendation':
      return allocatorProfileUrl(linkId);
    // Contest
    case 'new_contest_up_and_running':
    case 'contest_placed':
    case 'contest_finalist':
    case 'contest_winner':
      return contestUrl(linkId);
    // Fund/Employer
    case 'new_fund_recommendation':
      return employerProfileUrl(linkId);
    case 'comment_on_fund':
      return employerProfileVanityUrl(customLink);
    // Comment
    case 'tagged_on_idea_comment':
    case 'comment_on_idea':
    case 'comment_on_idea_with_response':
    case 'actively_discussed':
    case 'watchlist_entity_tagged_on_idea_comment':
      return ideaCommentsUrl(linkId);
    // Update
    case 'comment_on_idea_update':
    case 'idea_update_request':
    case 'idea_close_to_timeframe':
      return ideaUpdatesUrl(linkId);
    // Idea
    case 'idea_is_rated':
    case 'idea_autoclosed_timeframe':
    case 'idea_autoclosed_price_target':
    case 'trending_research':
    case 'biggest_movers':
    case 'new_followee_idea':
    case 'update_followee_idea':
      return ideaUrl(linkId);
    case 'new_idea_views':
      return contributorDashboardIdeasUrl(linkId);
    // Job Vault
    case 'job_application_received':
      return jobVaultAppsReceived(customLink);
    case 'new_job_posting':
      return jobVaultPostingUrl(linkId);
    // Message
    case 'intro_request':
      return messageUrl(linkId);
    // Post
    case 'tagged_on_post_comment':
    case 'comment_on_post':
    case 'watchlist_entity_tagged_on_post_activity':
    case 'watchlist_sector_tagged_on_post_activity':
    case 'new_followee_post':
      return postUrl(linkId);
    // Ranking
    case 'rankings_removed':
    case 'rankings_entered':
    case 'rankings_moved_down':
    case 'rankings_moved_up':
      return rankingsUrl();
    // User
    case 'follow_user':
    case 'new_fund_follower':
      return userUrl(linkId);
    case 'new_badge':
      return userUrl(currentUserId);
    // Watchlist
    case 'add_ticker_to_watchlist':
    case 'update_ticker_to_watchlist':
      return userWatchlistUrl();
    // White Paper
    case 'new_white_paper_of_interest':
      return whitePaperShowUrl(linkId);
    // Viewer Intro Request
    case 'new_viewer_intro_request':
      return userAccountViewerIntroRequestsUrl();
    case 'viewer_intro_request_accepted':
      return newViewerIntroRequestMessageUrl(linkId, customLink);
    default:
      return undefined;
  }
};

export const getIcon = (notificationClass, linkId) => {
  switch (notificationClass) {
    case 'comment_on_idea':
    case 'comment_on_idea_update':
    case 'comment_on_fund':
    case 'comment_on_post':
    case 'tagged_on_post_comment':
    case 'tagged_on_idea_comment':
      return MessageBubbleIcon;
    case 'new_contest_up_and_running':
      return TrophyIcon;
    case 'contest_winner':
      return ({ className, styles }) => (
        <TrophyIcon className={`${className} ${styles ? styles.contestWinner : ''}`} />
      );
    case 'contest_finalist':
      return ({ className, styles }) => (
        <TrophyIcon className={`${className} ${styles ? styles.contestFinalist : ''}`} />
      );
    case 'contest_placed':
      return ({ className, styles }) => (
        <TrophyIcon className={`${className} ${styles ? styles.placedInContest : ''}`} />
      );
    case 'rankings_removed':
    case 'rankings_moved_down':
      return DownArrowIcon;
    case 'rankings_entered':
    case 'rankings_moved_up':
      return UpArrowIcon;
    case 'idea_update_request':
      return ScaleIcon;
    case 'comment_on_idea_with_response':
      return MessageBubbleDoubleIcon;
    case 'new_idea_views':
      return EyeIcon;
    case 'idea_is_rated':
      return StarIcon;
    case 'new_badge':
      return badgeIconMapping[linkId];
    case 'idea_autoclosed_timeframe':
    case 'idea_close_to_timeframe':
      return ClockIcon;
    case 'idea_autoclosed_price_target':
      return DollarSignIcon;
    case 'intro_request':
      return EnvelopeIcon;
    case 'job_application_received':
      return EnvelopeIcon;
    case 'new_allocator_recommendation':
      return PieChartIcon;
    case 'new_fund_recommendation':
      return BriefcaseIcon;
    case 'add_ticker_to_watchlist':
    case 'update_ticker_to_watchlist':
      return LightbulbIcon;
    case 'actively_discussed':
      return MessageBubbleDoubleIcon;
    case 'trending_research':
      return TrendingIcon;
    case 'biggest_movers':
      return ArrowsUpDownIcon;
    case 'new_job_posting':
      return BriefcaseIcon;
    case 'new_white_paper_of_interest':
      return PaperStack;
    default:
      return null;
  }
};

export const getTag = notificationClass => {
  switch (notificationClass) {
    case 'add_ticker_to_watchlist':
      return 'notification-add-to-watchlist';
    case 'actively_discussed':
      return 'notification-actively-discussed';
    case 'biggest_movers':
      return 'notification-biggest-movers';
    case 'comment_on_post':
      return 'notification-new-comment-post';
    case 'comment_on_idea':
      return 'notification-new-comment';
    case 'comment_on_idea_with_response':
      return 'notification-new-comment-response-requested';
    case 'new_contest_up_and_running':
      return 'notification-new-contest';
    case 'new_badge':
      return 'notification-new-badge';
    case 'new_idea_views':
      return 'notification-idea-views';
    case 'idea_close_to_timeframe':
      return 'notification-idea-expiring';
    case 'new_allocator_recommendation':
      return 'notification-new-allocator';
    case 'new_fund_recommendation':
      return 'notification-new-fund';
    case 'idea_is_rated':
      return 'notification-idea-rating';
    case 'intro_request':
      return 'notification-fund-intro-request';
    case 'follow_user':
      return 'notification-new-follower';
    case 'update_ticker_to_watchlist':
      return 'notification-update-watchlist';
    case 'new_job_posting':
      return 'notification-new-job';
    case 'new_fund_follower':
      return 'notification-new-fund-follower';
    case 'trending_research':
      return 'notification-trending-research';
    case 'new_followee_idea':
      return 'notification-new-idea';
    case 'update_followee_idea':
      return 'notification-idea-update';
    case 'update_ticker_to_watchlist':
      return 'notification-update-watchlist';
    case 'tagged_on_idea_comment':
      return 'notification-tagged-idea-comment';
    case 'tagged_on_post_comment':
      return 'notification-tagged-post-comment';
    default:
      return null;
  }
};
